import axios, { AxiosError } from "axios";

/**
 * Sentry capture policy + fingerprinting for Axios failures (#2015).
 *
 * The shared response interceptor used to `Sentry.captureException` every
 * non-401 Axios failure with no fingerprint. Two problems followed:
 *
 *  1. **Over-grouping.** Every AxiosError is thrown from the same
 *     `axios/lib/core/settle.js` frame, so Sentry's default stack-based
 *     grouping collapsed 403s + 404s across *every* endpoint and *every*
 *     environment into one issue (NEXT-GEN-FRONTEND-2). A newly-broken
 *     endpoint never surfaced as a *new* issue, so the new-issue alert never
 *     fired for it.
 *  2. **Handled 4xx flood.** Routine, app-handled 4xx (a permission-gated page
 *     403, a deleted-resource 404, a validation 400/422) were captured as
 *     errors — ~95% of that bucket was staging / E2E traffic deliberately
 *     hitting forbidden pages.
 *
 * This module is the single source of truth for *what* we capture and *how* it
 * groups. It is pure (no Sentry SDK import, no browser globals beyond the ones
 * passed in) so the policy and the route normalization are unit-testable.
 */

/**
 * Endpoints that are semantically **reads** but use POST to carry their
 * arguments in a request body — a batch of permission checks, a set of
 * scheduling constraints, a workflow-graph position — because they don't fit a
 * query string. They have no side effects and are safe to retry, so a
 * no-response failure on one carries exactly as little signal as it would on a
 * GET, and is dropped the same way (#4095).
 *
 * Keys are **normalized routes** (see {@link normalizeRoute}), so they carry
 * `:slug` / `:uuid` rather than real ids. Add an entry only for an endpoint that
 * genuinely persists nothing — when in doubt leave it out, since the cost of
 * being wrong is a missed error rather than a noisy one.
 */
const READ_SHAPED_POST_ROUTES = new Set([
  // Batched `can` checks — the endpoint this issue surfaced on.
  "/api/workspaces/:slug/permissions/can",
  // Scheduling availability search.
  "/api/workspaces/:slug/find-available-times",
  // Intake workflow preview: "side-effect-free; nothing persists" per its own
  // service docstring.
  "/api/workspaces/:slug/intake/forms/:uuid/workflow/preview/advance",
  // Pre-auth lookup of which sign-in method an address uses. (Its siblings
  // `magic-generate` / `forgot-password` DO send mail — deliberately absent.)
  "/auth/email-check",
]);

/**
 * A failure worth a Sentry error: a real server fault or a connectivity
 * problem, not a response the client asked for and handles.
 *
 *  - **5xx** — the server errored. Signal.
 *  - **No-response failure on a mutation** — a POST/PATCH/PUT/DELETE that got a
 *    network error or client timeout may have *applied server-side*; the app
 *    can't safely retry it, so it's worth an error. A **read-shaped** POST
 *    (see {@link READ_SHAPED_POST_ROUTES}) is exempt: nothing could have
 *    applied, so there's nothing to warn about.
 *
 * Everything else is dropped to a breadcrumb (see {@link shouldCaptureAxiosError}):
 *  - **All 4xx** (401/403/404/400/409/422/429/…) — the server correctly
 *    applied its contract (auth / permission / not-found / validation / rate
 *    limit) and the app already handles the response. A 4xx is a *handled*
 *    result, not an unhandled FE error; capturing it buries real signal and
 *    lets test roles (deliberately hitting forbidden pages) flood the bucket.
 *  - **No-response failure on a safe request** — an idempotent GET, or a
 *    read-shaped POST (network error, or an `ECONNABORTED`/`ETIMEDOUT` client
 *    timeout) — overwhelmingly transport noise, not an app fault (#2756): a
 *    request stranded in flight when the laptop slept surfaces on wake as a
 *    wall-clock timeout / network error, with no backend record of the request.
 *    The app already handles the class (transient-retry on resets, recoverable
 *    loader error states, silent badge polls), and a *systemic* outage still
 *    surfaces as 5xx errors and warn-level logs. Capturing each stranded GET
 *    made every laptop-sleep a Sentry issue (the NEXT-GEN-FRONTEND-C/-3/-10…
 *    family); leaving read-shaped POSTs in did the same for permission checks
 *    (#4095).
 *  - **Canceled requests** (latest-wins guards, unmount, poll aborts) —
 *    intentional, never an error.
 *
 * The breadcrumb (and our existing `console.warn`/`console.error` → Sentry Logs
 * forwarding) keeps the trail: a dropped 4xx still shows up as context on the
 * *next* captured event, so nothing is silently swallowed.
 */
export function shouldCaptureAxiosError(error: unknown): boolean {
  // An intentionally-canceled request is never an error.
  if (axios.isCancel(error)) return false;

  const e = error as AxiosError;
  if (!e?.isAxiosError) {
    // Not an Axios error at all — let Sentry capture it (unexpected).
    return true;
  }

  const status = e.response?.status;
  const isTimeout = e.code === "ECONNABORTED" || e.code === "ETIMEDOUT";

  // No real HTTP answer (network error / client timeout): capture only for a
  // mutation, which may have applied server-side. A *safe* request's no-response
  // failure is transport noise (stranded-by-sleep, dead network) the app already
  // handles — breadcrumb only. Safe means an idempotent GET (#2756) or a POST
  // that only reads (#4095). An error whose config carries no method is an
  // unexpected shape; capture it rather than guess.
  if (status === undefined || isTimeout) {
    const method = e.config?.method?.toLowerCase();
    if (method === undefined) return true;
    if (method === "get") return false;
    // Scoped to POST on purpose: a PUT/PATCH/DELETE sharing one of these paths
    // would be a real mutation, and should still be captured.
    if (method === "post" && READ_SHAPED_POST_ROUTES.has(normalizeRoute(e.config?.url))) {
      return false;
    }
    return true;
  }

  // 5xx — server fault. Capture. 4xx — handled result. Breadcrumb only.
  return status >= 500;
}

/**
 * Collapse a request path's variable segments to placeholders so one endpoint
 * groups as one Sentry issue regardless of which record it hit. Without this,
 * `{method, status, route}` fingerprints would still fan out per record id.
 *
 * Rules (each replaces the whole segment):
 *  - UUID                    → `:uuid`
 *  - any segment containing a digit → `:id`
 *    Numeric ids (`12345`), stable-key / opaque keys (`abc123`), hashes /
 *    tokens (`deadbeef01`), and date-ish segments (`2026-07`) are all
 *    per-record — anything with a digit in it is treated as identifying.
 *
 * A purely-alphabetic segment (`requests`, `settings`, `workspace`) is a route
 * literal and is kept — it's low-cardinality and useful grouping context. Query
 * string and leading `/api` etc. are the caller's to strip; this only touches
 * path segments.
 *
 * The **workspace slug** is the exception: it's purely alphabetic often enough
 * to survive the rules above, but it identifies a *tenant*, so it's as
 * high-cardinality as our customer list. Keeping it fanned one broken endpoint
 * out into a separate issue — and a separate new-issue alert — per customer that
 * hit it (#4095), so it's collapsed to `:slug` by {@link normalizeRoute}.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HAS_DIGIT_RE = /\d/;

function normalizeSegment(segment: string): string {
  if (segment.length === 0) return segment;
  if (UUID_RE.test(segment)) return ":uuid";
  // Anything with a digit is record-identifying (numeric id, stable-key,
  // token/hash, date-ish); collapse so one endpoint groups regardless of id.
  if (HAS_DIGIT_RE.test(segment)) return ":id";
  return segment;
}

/**
 * Normalize a request URL into a stable route template for fingerprinting.
 * Strips the query string, drops a trailing empty segment (so `/foo/` and
 * `/foo` group together), normalizes each id-like segment, and collapses the
 * tenant slug so one endpoint is one issue across all workspaces.
 */
export function normalizeRoute(url: string | undefined): string {
  if (!url) return "unknown";
  // Drop query/hash; keep only the path.
  const path = url.split(/[?#]/, 1)[0] ?? url;
  const segments = path.split("/").map(normalizeSegment);
  // Collapse the tenant slug in `/api/workspaces/<slug>/…` to `:slug`. Matched
  // positionally rather than as "the segment after `workspaces`" so the literal
  // in that name's other position — `/api/users/me/workspaces/invitations/` —
  // keeps its own identity instead of merging into `:slug`.
  if (segments[1] === "api" && segments[2] === "workspaces" && segments[3]) {
    segments[3] = ":slug";
  }
  // Collapse a trailing slash (empty last segment) so `/x/` == `/x`, but keep
  // the leading empty segment's slash so absolute paths render as `/a/b`.
  const normalized = segments.join("/");
  return normalized.length > 1 && normalized.endsWith("/") ? normalized.slice(0, -1) : normalized;
}

/**
 * The fingerprint for a captured Axios error: `{method, status, route}`. An
 * explicit fingerprint array *replaces* Sentry's default stack-based grouping —
 * which is exactly the intent, so one endpoint = one issue instead of every
 * AxiosError merging on the shared `settle.js` throw frame. `{{ default }}` is
 * deliberately omitted so the settle.js frame can never re-merge the buckets.
 */
export function axiosFingerprint(error: unknown): string[] {
  const e = error as AxiosError;
  const method = (e?.config?.method ?? "unknown").toLowerCase();
  const status = e?.response?.status ?? (e?.code ? e.code : "network");
  const route = normalizeRoute(e?.config?.url);
  return ["axios", method, String(status), route];
}

/**
 * Whether this event originates from an automated (E2E / bot) browser. Playwright
 * — which drives our staging E2E suite — launches Chromium with the automation
 * flags that set `navigator.webdriver = true`, and no real user's browser reports
 * that. The SDK sends no PII by default (no email / user id), so `webdriver` is
 * the one runtime signal that's actually present for E2E traffic without leaking
 * anything. Dropping on it keeps the staging role-suite 403 flood (and the
 * main-suite `/` + `/workspace-invitations` hits) out of real signal (#2015).
 */
export function isAutomatedBrowser(): boolean {
  return typeof navigator !== "undefined" && navigator.webdriver === true;
}
